import {
  type ChangeEvent,
  type ReactElement,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import downloadJsonFile from './downloadJsonFile';
import {
  WatchTowerShell,
} from './TargetedMarketingChrome';
import useWatchTowerMock from './useWatchTowerMock';
import { skeletonWatchCompanyImport, toWatchCompanyImport } from './watchCompanyExport';
import WatchTowerCreateChatDialog from './WatchTowerCreateChatDialog';
import {
  filterWatchCompanies,
  formatAgentType,
  formatIsoDate,
  uniqueAgentTypes,
  uniqueCountries,
  uniqueIndustries,
  watchCompanyCoverageSummary,
} from './watchTowerSelectors';

import type {
  WatchCompany,
  WatchCompanyStatus,
  WatchRegistryCoverageFilter,
  WatchRegistryStatusFilter,
} from './types';

import styles from './scss/TargetedMarketing.module.scss';

function aiStatusClass(status: WatchCompany['aiStatus']): string {
  if (status === 'awaiting_human_review') {
    return styles.statusDraft;
  }
  if (status === 'review_ongoing') {
    return styles.statusActive;
  }
  if (status === 'in_error') {
    return styles.statusArchived;
  }
  if (status === 'human_accepted' || status === 'finalized') {
    return styles.statusActive;
  }
  return styles.statusArchived;
}

function statusClass(status: WatchCompanyStatus): string {
  if (status === 'active') {
    return styles.statusActive;
  }
  if (status === 'draft') {
    return styles.statusDraft;
  }
  return styles.statusArchived;
}

function WatchTowerRegistry(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state, update, liveActions,
  } = useWatchTowerMock();
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [agentType, setAgentType] = useState('');
  const [status, setStatus] = useState<WatchRegistryStatusFilter>('visible');
  const [coverageFilter, setCoverageFilter] = useState<WatchRegistryCoverageFilter>('all');
  const [createChatOpen, setCreateChatOpen] = useState(false);
  const [createChatId, setCreateChatId] = useState(0);

  const rows = useMemo(
    () => filterWatchCompanies(state, {
      search, country, industry, agentType, status, coverage: coverageFilter,
    }),
    [agentType, country, coverageFilter, industry, search, state, status],
  );

  const countries = useMemo(() => uniqueCountries(state), [state]);
  const industries = useMemo(() => uniqueIndustries(state), [state]);
  const agentTypes = useMemo(() => uniqueAgentTypes(state), [state]);
  const coverage = useMemo(() => watchCompanyCoverageSummary(state), [state]);

  const openCompany = (id: number) => {
    navigate(generatePath(AppRoutes.watchTowerCompany, { id: String(id) }));
  };

  const onNewCompany = async () => {
    try {
      const chat = await liveActions.openCreateChat();
      setCreateChatId(chat.id);
      setCreateChatOpen(true);
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onArchive = (id: number, nextStatus: WatchCompanyStatus) => {
    update((prev) => ({
      ...prev,
      watchCompanies: prev.watchCompanies.map((company) => (
        company.id === id ? { ...company, status: nextStatus } : company
      )),
    }));
    toast.success(nextStatus === 'archived'
      ? t('targetedMarketing.companyArchived')
      : t('targetedMarketing.companyRestored'));
  };

  const onExport = (id: number) => {
    const company = state.watchCompanies.find((item) => item.id === id);
    if (!company) {
      return;
    }
    downloadJsonFile(`watch-company-${company.uid || company.id}.json`, {
      schemaVersion: '1',
      exportedAt: new Date().toISOString(),
      sourceWatchCompanyId: company.id,
      companies: [toWatchCompanyImport(company)],
    });
    toast.success(t('targetedMarketing.exportStarted'));
  };

  const onDownloadSkeleton = () => {
    downloadJsonFile('watch-tower-template.json', skeletonWatchCompanyImport());
    toast.success(t('targetedMarketing.exportStarted'));
  };

  const onNativeChange = (setter: (value: string) => void) => (
    event: ChangeEvent<HTMLSelectElement>,
  ) => setter(event.target.value);

  const onCoverageClick = (next: WatchRegistryCoverageFilter) => {
    setStatus('visible');
    if (next === 'without_agents') {
      setAgentType('');
    }
    if (next === 'all') {
      setCoverageFilter('all');
      return;
    }
    setCoverageFilter((current) => (current === next ? 'all' : next));
  };

  const coverageCardClass = (value: WatchRegistryCoverageFilter): string => {
    const selected = coverageFilter === value
      ? ` ${styles.kpiCardActive}`
      : '';
    return `${styles.kpiCard} ${styles.kpiCardButton}${selected}`;
  };

  return (
    <WatchTowerShell
      primary="companies"
      runsSubView={null}
      actions={(
        <div className={styles.toolbarActions}>
          <Button
            title={t('targetedMarketing.import')}
            variant={BUTTON_VARIANT.secondary}
            onClick={() => navigate(AppRoutes.watchTowerImport)}
          />
          <Button
            title={t('targetedMarketing.downloadTemplate')}
            variant={BUTTON_VARIANT.secondary}
            onClick={onDownloadSkeleton}
          />
          <Button
            title={t('targetedMarketing.newCompany')}
            startIcon={<PlusIcon />}
            onClick={onNewCompany}
          />
        </div>
      )}
    >
      <div className={styles.kpiRow}>
        <button
          type="button"
          className={coverageCardClass('all')}
          aria-pressed={coverageFilter === 'all'}
          aria-label={t('targetedMarketing.summaryCompanies')}
          onClick={() => onCoverageClick('all')}
        >
          <p className={styles.kpiLabel}>{t('targetedMarketing.summaryCompanies')}</p>
          <p className={styles.kpiValue}>{coverage.visibleCount}</p>
        </button>
        <button
          type="button"
          className={coverageCardClass('with_agents')}
          aria-pressed={coverageFilter === 'with_agents'}
          aria-label={t('targetedMarketing.summaryWithAgents')}
          onClick={() => onCoverageClick('with_agents')}
        >
          <p className={styles.kpiLabel}>{t('targetedMarketing.summaryWithAgents')}</p>
          <p className={styles.kpiValue}>{coverage.withAgentsCount}</p>
        </button>
        <button
          type="button"
          className={coverageCardClass('without_agents')}
          aria-pressed={coverageFilter === 'without_agents'}
          aria-label={t('targetedMarketing.summaryWithoutAgents')}
          onClick={() => onCoverageClick('without_agents')}
        >
          <p className={styles.kpiLabel}>{t('targetedMarketing.summaryWithoutAgents')}</p>
          <p className={styles.kpiValue}>{coverage.withoutAgentsCount}</p>
        </button>
        <button
          type="button"
          className={coverageCardClass('awaiting_review')}
          aria-pressed={coverageFilter === 'awaiting_review'}
          aria-label={t('targetedMarketing.summaryAwaitingReview')}
          onClick={() => onCoverageClick('awaiting_review')}
        >
          <p className={styles.kpiLabel}>{t('targetedMarketing.summaryAwaitingReview')}</p>
          <p className={styles.kpiValue}>{coverage.awaitingReviewCount}</p>
        </button>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <label className={styles.searchField} htmlFor="watch-tower-search">
            <SearchIcon />
            <input
              id="watch-tower-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('targetedMarketing.searchCompanies')}
            />
          </label>
          <select
            className={styles.filterNative}
            aria-label={t('targetedMarketing.country')}
            value={country}
            onChange={onNativeChange(setCountry)}
          >
            <option value="">{t('targetedMarketing.allCountries')}</option>
            {countries.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <select
            className={styles.filterNative}
            aria-label={t('targetedMarketing.industry')}
            value={industry}
            onChange={onNativeChange(setIndustry)}
          >
            <option value="">{t('targetedMarketing.allIndustries')}</option>
            {industries.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <select
            className={styles.filterNative}
            aria-label={t('targetedMarketing.agentCoverage')}
            value={agentType}
            onChange={onNativeChange(setAgentType)}
          >
            <option value="">{t('targetedMarketing.allAgents')}</option>
            {agentTypes.map((item) => (
              <option key={item} value={item}>{formatAgentType(item)}</option>
            ))}
          </select>
          <select
            className={styles.filterNative}
            aria-label={t('targetedMarketing.status')}
            value={status}
            onChange={(event) => setStatus(event.target.value as WatchRegistryStatusFilter)}
          >
            <option value="visible">{t('targetedMarketing.statusVisible')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="draft">{t('common.draft')}</option>
            <option value="archived">{t('targetedMarketing.archive')}</option>
            <option value="all">{t('targetedMarketing.statusAll')}</option>
          </select>
        </div>
      </div>

      <Panel className={styles.panel}>
        {rows.length === 0 ? (
          <EmptyList text={t('targetedMarketing.emptyCompanies')}>
            <p className={styles.muted}>{t('targetedMarketing.emptyCta')}</p>
          </EmptyList>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('targetedMarketing.name')}</th>
                  <th>{t('targetedMarketing.country')}</th>
                  <th>{t('targetedMarketing.industry')}</th>
                  <th>{t('targetedMarketing.identifiers')}</th>
                  <th>{t('targetedMarketing.products')}</th>
                  <th>{t('targetedMarketing.buildings')}</th>
                  <th>{t('targetedMarketing.agents')}</th>
                  <th>{t('targetedMarketing.lastRun')}</th>
                  <th>{t('targetedMarketing.openFindings')}</th>
                  <th>{t('targetedMarketing.status')}</th>
                  <th>{t('targetedMarketing.aiStatus')}</th>
                  <th>{t('targetedMarketing.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.company.id}>
                    <td>
                      <button
                        type="button"
                        className={styles.nameButton}
                        onClick={() => openCompany(row.company.id)}
                      >
                        {row.company.name}
                      </button>
                    </td>
                    <td>{row.company.addressCountry || t('targetedMarketing.none')}</td>
                    <td>{row.company.naceSection || t('targetedMarketing.none')}</td>
                    <td>{row.identifierLabel}</td>
                    <td>{row.productCount}</td>
                    <td>{row.buildingCount}</td>
                    <td>
                      {row.agentTypes.length
                        ? row.agentTypes.map(formatAgentType).join(', ')
                        : t('targetedMarketing.none')}
                    </td>
                    <td>{formatIsoDate(row.lastRunAt)}</td>
                    <td>{row.openFindingCount}</td>
                    <td>
                      <span className={`${styles.statusPill} ${statusClass(row.company.status)}`}>
                        {row.company.status}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.statusPill} ${aiStatusClass(row.company.aiStatus)}`}>
                        {t(`targetedMarketing.aiStatus_${row.company.aiStatus}`)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.openDossier')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => openCompany(row.company.id)}
                        />
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.scheduleAgents')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => navigate(
                            `${AppRoutes.watchTowerSchedules}?companyId=${row.company.id}`,
                          )}
                        />
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.exportCompany')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => onExport(row.company.id)}
                        />
                        {row.company.status === 'archived' ? (
                          <Button
                            className={styles.rowAction}
                            title={t('targetedMarketing.unarchive')}
                            variant={BUTTON_VARIANT.secondary}
                            onClick={() => onArchive(row.company.id, 'active')}
                          />
                        ) : (
                          <Button
                            className={styles.rowAction}
                            title={t('targetedMarketing.archive')}
                            variant={BUTTON_VARIANT.error}
                            onClick={() => onArchive(row.company.id, 'archived')}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {createChatOpen && createChatId ? (
        <WatchTowerCreateChatDialog
          open={createChatOpen}
          chatId={createChatId}
          state={state}
          onClose={() => setCreateChatOpen(false)}
          onApply={update}
        />
      ) : null}
    </WatchTowerShell>
  );
}

export default WatchTowerRegistry;
